import React, { useState, useCallback, useEffect, useId } from 'react';
import { cva } from 'class-variance-authority';
import { motion, AnimatePresence } from 'motion/react';
import { duration } from './primitives/tokens';
import { Portal } from './primitives/Portal';
import { useFloating } from '../../hooks/useFloating';
import { Button } from './Button';
import { Icon } from './Icon';
import { zIndex as Z } from '../../tokens/zIndex';
import styles from './Popconfirm.module.css';

type Placement =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end'
  | 'left-start'
  | 'left-end'
  | 'right-start'
  | 'right-end';

interface PopconfirmProps {
  /** Confirmation question */
  title: React.ReactNode;
  /** Additional description */
  description?: React.ReactNode;
  /** Called when confirmed */
  onConfirm?: () => void;
  /** Called when cancelled */
  onCancel?: () => void;
  /** OK button text */
  okText?: string;
  /** Cancel button text */
  cancelText?: string;
  /** OK button as danger */
  okDanger?: boolean;
  /** Position relative to trigger */
  placement?: Placement;
  /** Custom icon */
  icon?: React.ReactNode;
  /** Controlled open state */
  open?: boolean;
  /** Called when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Trigger element */
  children: React.ReactElement;
  /** Allow forwarding other props (e.g. from Tooltip) */
  [key: string]: unknown;
}

const popconfirmPanel = cva(styles.panel);

/**
 * Popconfirm — confirmation popup before an action.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | title | ReactNode | — | Question text |
 * | description | ReactNode | — | Extra description |
 * | onConfirm | () => void | — | OK callback |
 * | onCancel | () => void | — | Cancel callback |
 * | okText | string | 'Yes' | OK label |
 * | cancelText | string | 'No' | Cancel label |
 * | okDanger | boolean | false | Danger OK button |
 * | placement | Placement | 'top' | Position |
 * | icon | ReactNode | Warning icon | Custom icon |
 * | open | boolean | — | Controlled state |
 * | onOpenChange | (open) => void | — | State callback |
 *
 * @example
 * <Popconfirm title="Delete this thought?" onConfirm={handleDelete} okDanger>
 *   <Button danger>Delete</Button>
 * </Popconfirm>
 */
export function Popconfirm({
  title,
  description,
  onConfirm,
  onCancel,
  okText = 'Yes',
  cancelText = 'No',
  okDanger = false,
  placement = 'top',
  icon,
  open: controlledOpen,
  onOpenChange,
  children,
  ...rest
}: PopconfirmProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;
  const titleId = useId();

  const { triggerRef, floatingRef, position } = useFloating<HTMLElement, HTMLDivElement>({
    placement,
    offset: 12,
    open: isOpen,
  });

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const handleClick = useCallback(() => {
    setOpen(!isOpen);
  }, [isOpen, setOpen]);

  const handleConfirm = useCallback(() => {
    onConfirm?.();
    setOpen(false);
  }, [onConfirm, setOpen]);

  const handleCancel = useCallback(() => {
    onCancel?.();
    setOpen(false);
  }, [onCancel, setOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (triggerRef.current?.contains(e.target as Node)) return;
      if (floatingRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen, setOpen, triggerRef, floatingRef]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, setOpen]);

  const defaultIcon = <Icon name="warning" size={16} className={styles.defaultIconColor} />;

  const originalOnClick = (children as React.ReactElement<{ onClick?: React.MouseEventHandler }>).props.onClick;

  return (
    <>
      {React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
        ...rest,
        ref: triggerRef,
        onClick: (e: React.MouseEvent) => {
          originalOnClick?.(e);
          handleClick();
        },
      })}
      <AnimatePresence>
        {isOpen && (
          <Portal>
            <motion.div
              ref={floatingRef}
              role="alertdialog"
              aria-modal="true"
              aria-labelledby={titleId}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: duration.normal / 1000 }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              className={popconfirmPanel()}
              style={{
                '--popconfirm-top': `${position.top}px`,
                '--popconfirm-left': `${position.left}px`,
                '--popconfirm-z': Z.popover,
              } as React.CSSProperties}
            >
              <div className={styles.header}>
                <span aria-hidden="true" className={styles.headerIcon}>{icon ?? defaultIcon}</span>
                <div className={styles.titleWrapper}>
                  <div id={titleId} className={styles.title}>
                    {title}
                  </div>
                  {description && (
                    <div className={styles.description}>
                      {description}
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.footer}>
                <Button size="sm" variant="text" onClick={handleCancel}>
                  {cancelText}
                </Button>
                <Button size="sm" variant="primary" danger={okDanger} onClick={handleConfirm}>
                  {okText}
                </Button>
              </div>
            </motion.div>
          </Portal>
        )}
      </AnimatePresence>
    </>
  );
}
